import React, { useState, useEffect, useCallback } from 'react';
import { 
  statisticsService,
  accountService,
  transferService
} from '../../../apiServices';
import { useRefresh } from '../../../contexts/RefreshContext';
import { 
  CategoryStatisticsResponse,
  StatisticsFilters,
  Account,
  Transfer,
  TransferType
} from '../../../apiServices/types';
import { formatCurrency, getDateRange, getAvailableMonths, getAvailableYears, getAvailableWeeks, getCurrentWeek, formatDate, parseWeek, formatWeek } from '../../../utils';
import { GlassSelect } from '../../UI';
import { CategoryTransactionsModal } from './components';
import './StatisticsPage.css';

type StatisticsType = 'expenses' | 'income' | 'balance' | 'comparison';

const StatisticsPage: React.FC = () => {
  const [statisticsType, setStatisticsType] = useState<StatisticsType>('expenses');
  const [statistics, setStatistics] = useState<CategoryStatisticsResponse | null>(null);
  const [incomeStatistics, setIncomeStatistics] = useState<CategoryStatisticsResponse | null>(null);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [filters, setFilters] = useState<StatisticsFilters>({
    period: 'month'
  });
  const [selectedCategory, setSelectedCategory] = useState<{ id: string; name: string; type?: 'expenses' | 'income' } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  
  // Comparison state
  const [comparisonType, setComparisonType] = useState<'income' | 'expenses' | 'savings'>('expenses');
  const [comparisonPeriod, setComparisonPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [comparisonScope, setComparisonScope] = useState<'global' | 'category'>('global');
  const [firstPeriodWeek, setFirstPeriodWeek] = useState<string>('');
  const [firstPeriodMonth, setFirstPeriodMonth] = useState<number | undefined>(undefined);
  const [firstPeriodYear, setFirstPeriodYear] = useState<number | undefined>(undefined);
  const [secondPeriodWeek, setSecondPeriodWeek] = useState<string>('');
  const [secondPeriodMonth, setSecondPeriodMonth] = useState<number | undefined>(undefined);
  const [secondPeriodYear, setSecondPeriodYear] = useState<number | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<'name' | 'first' | 'second' | 'change' | 'percentage'>('change');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPeriodData, setCurrentPeriodData] = useState<CategoryStatisticsResponse | null>(null);
  const [previousPeriodData, setPreviousPeriodData] = useState<CategoryStatisticsResponse | null>(null);
  const [currentPeriodTransfers, setCurrentPeriodTransfers] = useState<Transfer[]>([]);
  const [previousPeriodTransfers, setPreviousPeriodTransfers] = useState<Transfer[]>([]);
  const [currentPeriodIncome, setCurrentPeriodIncome] = useState<CategoryStatisticsResponse | null>(null);
  const [previousPeriodIncome, setPreviousPeriodIncome] = useState<CategoryStatisticsResponse | null>(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    // Завантажуємо доступні дати при зміні типу статистики або рахунку
    loadAvailableDates();
  }, [statisticsType, filters.accountId]);

  useEffect(() => {
    // Перевіряємо доступність обраного періоду і переключаємо на доступний якщо потрібно
    if (statisticsType === 'comparison') {
      const availableWeeks = getAvailableWeeks(availableDates);
      const availableYears = getAvailableYears(availableDates);
      const hasMultipleWeeks = availableWeeks.length > 1;
      const hasMultipleYears = availableYears.length > 1;
      
      let hasMultipleMonths = false;
      if (availableYears.length > 1) {
        hasMultipleMonths = true;
      } else if (availableYears.length === 1) {
        const months = getAvailableMonths(availableDates, availableYears[0].value);
        hasMultipleMonths = months.length > 1;
      }
      
      // Якщо обраний період недоступний, переключаємо на доступний
      if (comparisonPeriod === 'week' && !hasMultipleWeeks) {
        if (hasMultipleMonths) {
          setComparisonPeriod('month');
        } else if (hasMultipleYears) {
          setComparisonPeriod('year');
        }
      } else if (comparisonPeriod === 'month' && !hasMultipleMonths) {
        if (hasMultipleWeeks) {
          setComparisonPeriod('week');
        } else if (hasMultipleYears) {
          setComparisonPeriod('year');
        }
      } else if (comparisonPeriod === 'year' && !hasMultipleYears) {
        if (hasMultipleWeeks) {
          setComparisonPeriod('week');
        } else if (hasMultipleMonths) {
          setComparisonPeriod('month');
        }
      }
    }
  }, [statisticsType, comparisonPeriod, availableDates]);

  useEffect(() => {
    // Ініціалізуємо значення періодів порівняння при зміні типу періоду
    // Другий період завжди актуальний на сьогодні, перший - попередній
    if (statisticsType === 'comparison') {
      if (comparisonPeriod === 'week') {
        const availableWeeks = getAvailableWeeks(availableDates);
        const currentWeek = getCurrentWeek();
        const currentWeekExists = availableWeeks.some(w => w.value === currentWeek);
        
        // Другий період - поточний тиждень (якщо є в доступних) або перший доступний
        if (availableWeeks.length > 0 && !secondPeriodWeek) {
          setSecondPeriodWeek(currentWeekExists ? currentWeek : availableWeeks[0].value);
        }
        
        // Перший період - попередній тиждень від другого
        if (availableWeeks.length > 1 && !firstPeriodWeek && secondPeriodWeek) {
          const { year, week } = parseWeek(secondPeriodWeek);
          const prevWeek = week === 1 ? { year: year - 1, week: 52 } : { year, week: week - 1 };
          const prevWeekStr = formatWeek(prevWeek.year, prevWeek.week);
          const prevWeekExists = availableWeeks.some(w => w.value === prevWeekStr);
          if (prevWeekExists) {
            setFirstPeriodWeek(prevWeekStr);
          } else if (availableWeeks.length > 1) {
            setFirstPeriodWeek(availableWeeks[1].value);
          }
        }
      } else if (comparisonPeriod === 'month') {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        const availableYears = getAvailableYears(availableDates);
        const currentYearExists = availableYears.some(y => y.value === currentYear);
        
        // Другий період - поточний місяць/рік
        if (secondPeriodYear === undefined && currentYearExists) {
          setSecondPeriodYear(currentYear);
          const availableMonths = getAvailableMonths(availableDates, currentYear);
          const currentMonthExists = availableMonths.some(m => m.value === currentMonth);
          if (currentMonthExists && secondPeriodMonth === undefined) {
            setSecondPeriodMonth(currentMonth);
          } else if (availableMonths.length > 0 && secondPeriodMonth === undefined) {
            setSecondPeriodMonth(availableMonths[0].value);
          }
        } else if (secondPeriodYear === undefined && availableYears.length > 0) {
          setSecondPeriodYear(availableYears[0].value);
          const availableMonths = getAvailableMonths(availableDates, availableYears[0].value);
          if (availableMonths.length > 0 && secondPeriodMonth === undefined) {
            setSecondPeriodMonth(availableMonths[0].value);
          }
        }
        
        // Перший період - попередній місяць від другого
        if (secondPeriodYear !== undefined && secondPeriodMonth !== undefined && firstPeriodYear === undefined) {
          if (secondPeriodMonth === 0) {
            const prevYear = secondPeriodYear - 1;
            const prevYearExists = availableYears.some(y => y.value === prevYear);
            if (prevYearExists) {
              setFirstPeriodYear(prevYear);
              const availableMonths = getAvailableMonths(availableDates, prevYear);
              if (availableMonths.length > 0) {
                setFirstPeriodMonth(availableMonths[0].value); // Останній місяць року (грудень = 11)
              }
            } else if (availableYears.length > 1) {
              setFirstPeriodYear(availableYears[1].value);
              const availableMonths = getAvailableMonths(availableDates, availableYears[1].value);
              if (availableMonths.length > 0) {
                setFirstPeriodMonth(availableMonths[0].value);
              }
            }
          } else {
            setFirstPeriodYear(secondPeriodYear);
            setFirstPeriodMonth(secondPeriodMonth - 1);
          }
        }
      } else if (comparisonPeriod === 'year') {
        const availableYears = getAvailableYears(availableDates);
        const currentYear = new Date().getFullYear();
        const currentYearExists = availableYears.some(y => y.value === currentYear);
        
        // Другий період - поточний рік
        if (availableYears.length > 0 && secondPeriodYear === undefined) {
          setSecondPeriodYear(currentYearExists ? currentYear : availableYears[0].value);
        }
        
        // Перший період - попередній рік від другого
        if (secondPeriodYear !== undefined && firstPeriodYear === undefined) {
          const prevYear = secondPeriodYear - 1;
          const prevYearExists = availableYears.some(y => y.value === prevYear);
          if (prevYearExists) {
            setFirstPeriodYear(prevYear);
          } else if (availableYears.length > 1) {
            setFirstPeriodYear(availableYears[1].value);
          }
        }
      }
    }
  }, [statisticsType, comparisonPeriod, availableDates, firstPeriodWeek, firstPeriodMonth, firstPeriodYear, secondPeriodWeek, secondPeriodMonth, secondPeriodYear]);

  useEffect(() => {
    // Якщо переключаємося на доходи, баланс або порівняння і вибраний тиждень, автоматично встановлюємо місяць
    if ((statisticsType === 'income' || statisticsType === 'balance' || statisticsType === 'comparison') && filters.period === 'week') {
      // Встановлюємо останній доступний місяць
      const availableYears = getAvailableYears(availableDates);
      if (availableYears.length > 0) {
        const lastYear = availableYears[0].value;
        const availableMonths = getAvailableMonths(availableDates, lastYear);
        if (availableMonths.length > 0) {
          setFilters(prev => ({ 
            ...prev, 
            period: 'month',
            selectedMonth: availableMonths[0].value,
            selectedYear: lastYear
          }));
        } else {
          setFilters(prev => ({ 
            ...prev, 
            period: 'month',
            selectedYear: lastYear
          }));
        }
      }
      return;
    }
  }, [statisticsType, filters.period, availableDates]);

  useEffect(() => {
    // Ініціалізуємо значення за замовчуванням при зміні періоду
    if (filters.period === 'week' && !filters.selectedWeek && statisticsType !== 'income' && statisticsType !== 'balance') {
      // Встановлюємо поточний тиждень або перший доступний
      const availableWeeks = getAvailableWeeks(availableDates);
      if (availableWeeks.length > 0) {
        const currentWeek = getCurrentWeek();
        const weekExists = availableWeeks.some(w => w.value === currentWeek);
        setFilters(prev => ({ 
          ...prev, 
          selectedWeek: weekExists ? currentWeek : availableWeeks[0].value
        }));
      }
    } else if (filters.period === 'month' && (filters.selectedMonth === undefined || filters.selectedYear === undefined)) {
      const availableYears = getAvailableYears(availableDates);
      if (availableYears.length > 0) {
        // Беремо останній доступний рік (перший в масиві, бо вони відсортовані від новіших до старіших)
        const selectedYear = availableYears[0].value;
        
        const availableMonths = getAvailableMonths(availableDates, selectedYear);
        if (availableMonths.length > 0) {
          // Беремо останній доступний місяць (перший в масиві, бо вони відсортовані від новіших до старіших)
          setFilters(prev => ({ 
            ...prev, 
            selectedYear,
            selectedMonth: availableMonths[0].value
          }));
        } else {
          setFilters(prev => ({ 
            ...prev, 
            selectedYear
          }));
        }
      }
    } else if (filters.period === 'year' && filters.selectedYear === undefined) {
      const availableYears = getAvailableYears(availableDates);
      if (availableYears.length > 0) {
        const currentYear = new Date().getFullYear();
        const yearExists = availableYears.some(y => y.value === currentYear);
        setFilters(prev => ({ 
          ...prev, 
          selectedYear: yearExists ? currentYear : availableYears[0].value
        }));
      }
    }
  }, [statisticsType, filters.period, availableDates]);

  useEffect(() => {
    // Перевіряємо чи фільтри ініціалізовані перед завантаженням статистики
    // Для місячного періоду потрібні selectedMonth та selectedYear
    if (filters.period === 'month' && (filters.selectedMonth === undefined || filters.selectedYear === undefined)) {
      return;
    }
    // Для тижневого періоду (якщо не income/balance) потрібен selectedWeek
    if (filters.period === 'week' && !filters.selectedWeek && statisticsType !== 'income' && statisticsType !== 'balance') {
      return;
    }
    // Для річного періоду потрібен selectedYear
    if (filters.period === 'year' && filters.selectedYear === undefined) {
      return;
    }
    // Для custom періоду потрібні dateFrom та dateTo
    if (filters.period === 'custom' && (!filters.dateFrom || !filters.dateTo)) {
      return;
    }
    
    // Завантажуємо статистику в залежності від типу
    if (statisticsType === 'expenses') {
      loadExpensesStatistics();
    } else if (statisticsType === 'income') {
      loadIncomeStatistics();
    } else if (statisticsType === 'balance') {
      loadBalanceStatistics();
    } else if (statisticsType === 'comparison') {
      loadComparisonStatistics();
    }
  }, [filters, statisticsType, comparisonType, comparisonPeriod, comparisonScope, firstPeriodWeek, firstPeriodMonth, firstPeriodYear, secondPeriodWeek, secondPeriodMonth, secondPeriodYear]);

  const loadExpensesStatistics = async () => {
    setLoading(true);
    try {
      const dateRange = filters.period !== 'custom' 
        ? getDateRange(
            filters.period,
            filters.selectedMonth,
            filters.selectedYear,
            filters.selectedWeek
          )
        : { dateFrom: filters.dateFrom!, dateTo: filters.dateTo! };
      
      const data = await statisticsService.getCategoryStatistics({
        ...filters,
        ...dateRange
      });
      
      setStatistics(data);
    } catch (error) {
      console.error('Failed to load expenses statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadIncomeStatistics = async () => {
    setLoading(true);
    try {
      const dateRange = filters.period !== 'custom' 
        ? getDateRange(
            filters.period,
            filters.selectedMonth,
            filters.selectedYear,
            filters.selectedWeek
          )
        : { dateFrom: filters.dateFrom!, dateTo: filters.dateTo! };
      
      const data = await statisticsService.getIncomeCategoryStatistics({
        ...filters,
        ...dateRange
      });
      
      setIncomeStatistics(data);
    } catch (error) {
      console.error('Failed to load income statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBalanceStatistics = async () => {
    setLoading(true);
    try {
      const dateRange = filters.period !== 'custom' 
        ? getDateRange(
            filters.period,
            filters.selectedMonth,
            filters.selectedYear,
            filters.selectedWeek
          )
        : { dateFrom: filters.dateFrom!, dateTo: filters.dateTo! };
      
      const [expensesData, incomeData, filteredTransfers] = await Promise.all([
        statisticsService.getCategoryStatistics({
          ...filters,
          ...dateRange
        }),
        statisticsService.getIncomeCategoryStatistics({
          ...filters,
          ...dateRange
        }),
        transferService.getFiltered({
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo,
          accountId: filters.accountId,
          transferType: TransferType.Savings
        })
      ]);
      
      setStatistics(expensesData);
      setIncomeStatistics(incomeData);
      setTransfers(filteredTransfers);
    } catch (error) {
      console.error('Failed to load balance statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPreviousPeriodDateRange = (
    period: 'week' | 'month' | 'year',
    selectedMonth?: number,
    selectedYear?: number,
    selectedWeek?: string
  ): { dateFrom: string; dateTo: string } => {
    let currentDateFrom: Date;
    let currentDateTo: Date;
    
    // Спочатку отримуємо поточний період
    const currentRange = getDateRange(period, selectedMonth, selectedYear, selectedWeek);
    currentDateFrom = new Date(currentRange.dateFrom);
    currentDateTo = new Date(currentRange.dateTo);
    
    // Обчислюємо попередній період
    let previousDateFrom: Date;
    let previousDateTo: Date;
    
    switch (period) {
      case 'week': {
        // Віднімаємо 7 днів
        previousDateFrom = new Date(currentDateFrom);
        previousDateFrom.setDate(previousDateFrom.getDate() - 7);
        previousDateTo = new Date(currentDateTo);
        previousDateTo.setDate(previousDateTo.getDate() - 7);
        break;
      }
      case 'month': {
        // Віднімаємо 1 місяць
        previousDateFrom = new Date(currentDateFrom);
        previousDateFrom.setMonth(previousDateFrom.getMonth() - 1);
        previousDateTo = new Date(previousDateFrom.getFullYear(), previousDateFrom.getMonth() + 1, 0);
        break;
      }
      case 'year': {
        // Віднімаємо 1 рік
        previousDateFrom = new Date(currentDateFrom);
        previousDateFrom.setFullYear(previousDateFrom.getFullYear() - 1);
        previousDateTo = new Date(previousDateFrom.getFullYear(), 11, 31);
        break;
      }
      default:
        previousDateFrom = currentDateFrom;
        previousDateTo = currentDateTo;
    }
    
    const formatDateOnly = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      dateFrom: formatDateOnly(previousDateFrom),
      dateTo: formatDateOnly(previousDateTo)
    };
  };

  const loadComparisonStatistics = async () => {
    setLoading(true);
    try {
      // Отримуємо другий період (завжди актуальний на сьогодні)
      let secondRange: { dateFrom: string; dateTo: string };
      
      if (comparisonPeriod === 'week') {
        const weekToUse = secondPeriodWeek || getCurrentWeek();
        secondRange = getDateRange('week', undefined, undefined, weekToUse);
      } else if (comparisonPeriod === 'month') {
        const now = new Date();
        const monthToUse = secondPeriodMonth !== undefined ? secondPeriodMonth : now.getMonth();
        const yearToUse = secondPeriodYear !== undefined ? secondPeriodYear : now.getFullYear();
        secondRange = getDateRange('month', monthToUse, yearToUse);
      } else {
        const yearToUse = secondPeriodYear !== undefined ? secondPeriodYear : new Date().getFullYear();
        secondRange = getDateRange('year', undefined, yearToUse);
      }
      
      // Отримуємо перший період (попередній від другого)
      let firstRange: { dateFrom: string; dateTo: string };
      
      if (comparisonPeriod === 'week') {
        const weekToUse = firstPeriodWeek || (() => {
          // Якщо не обрано, беремо попередній тиждень від другого
          const secondWeek = secondPeriodWeek || getCurrentWeek();
          const { year, week } = parseWeek(secondWeek);
          const prevWeek = week === 1 ? { year: year - 1, week: 52 } : { year, week: week - 1 };
          return formatWeek(prevWeek.year, prevWeek.week);
        })();
        firstRange = getDateRange('week', undefined, undefined, weekToUse);
      } else if (comparisonPeriod === 'month') {
        let monthToUse: number;
        let yearToUse: number;
        
        if (firstPeriodMonth !== undefined && firstPeriodYear !== undefined) {
          monthToUse = firstPeriodMonth;
          yearToUse = firstPeriodYear;
        } else {
          // Якщо не обрано, беремо попередній місяць від другого
          const secondMonth = secondPeriodMonth !== undefined ? secondPeriodMonth : new Date().getMonth();
          const secondYear = secondPeriodYear !== undefined ? secondPeriodYear : new Date().getFullYear();
          
          if (secondMonth === 0) {
            monthToUse = 11;
            yearToUse = secondYear - 1;
          } else {
            monthToUse = secondMonth - 1;
            yearToUse = secondYear;
          }
        }
        
        firstRange = getDateRange('month', monthToUse, yearToUse);
      } else {
        const yearToUse = firstPeriodYear !== undefined ? firstPeriodYear : (() => {
          const secondYear = secondPeriodYear !== undefined ? secondPeriodYear : new Date().getFullYear();
          return secondYear - 1;
        })();
        firstRange = getDateRange('year', undefined, yearToUse);
      }
      
      // Для порівняння завжди використовуємо всі рахунки (не передаємо accountId)
      const comparisonFilters = {
        ...filters,
        accountId: undefined
      };

      if (comparisonType === 'expenses') {
        const [firstData, secondData] = await Promise.all([
          statisticsService.getCategoryStatistics({
            ...comparisonFilters,
            ...firstRange
          }),
          statisticsService.getCategoryStatistics({
            ...comparisonFilters,
            ...secondRange
          })
        ]);
        setCurrentPeriodData(firstData);
        setPreviousPeriodData(secondData);
      } else if (comparisonType === 'income') {
        const [firstData, secondData] = await Promise.all([
          statisticsService.getIncomeCategoryStatistics({
            ...comparisonFilters,
            ...firstRange
          }),
          statisticsService.getIncomeCategoryStatistics({
            ...comparisonFilters,
            ...secondRange
          })
        ]);
        setCurrentPeriodIncome(firstData);
        setPreviousPeriodIncome(secondData);
      } else if (comparisonType === 'savings') {
        const [firstTransfers, secondTransfers] = await Promise.all([
          transferService.getFiltered({
            dateFrom: firstRange.dateFrom,
            dateTo: firstRange.dateTo,
            accountId: undefined,
            transferType: TransferType.Savings
          }),
          transferService.getFiltered({
            dateFrom: secondRange.dateFrom,
            dateTo: secondRange.dateTo,
            accountId: undefined,
            transferType: TransferType.Savings
          })
        ]);
        setCurrentPeriodTransfers(firstTransfers);
        setPreviousPeriodTransfers(secondTransfers);
      }
    } catch (error) {
      console.error('Failed to load comparison statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await accountService.getAll();
      setAccounts(data);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const loadAvailableDates = async () => {
    setLoadingDates(true);
    try {
      const type = statisticsType === 'expenses' ? 'expenses' : statisticsType === 'income' ? 'income' : undefined;
      const data = await statisticsService.getAvailableDates(type, filters.accountId);
      setAvailableDates(data.dates);
    } catch (error) {
      console.error('Failed to load available dates:', error);
      setAvailableDates([]);
    } finally {
      setLoadingDates(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (statisticsType === 'expenses') {
      await loadExpensesStatistics();
    } else if (statisticsType === 'income') {
      await loadIncomeStatistics();
    } else if (statisticsType === 'balance') {
      await loadBalanceStatistics();
    } else if (statisticsType === 'comparison') {
      await loadComparisonStatistics();
    }
  }, [
    statisticsType,
    filters,
    comparisonType,
    comparisonPeriod,
    comparisonScope,
    firstPeriodWeek,
    firstPeriodMonth,
    firstPeriodYear,
    secondPeriodWeek,
    secondPeriodMonth,
    secondPeriodYear
  ]);

  useRefresh(handleRefresh);

  return (
    <div className="statistics-page">
      <div className="statistics-page-header">
        <div className="statistics-page-header-content">
          <h2 className="statistics-page-header-title">Статистика</h2>
          <p className="statistics-page-header-subtitle">Аналізуйте свої витрати та доходи</p>
        </div>
      </div>

      <div className="card statistics-filters-card">

        {/* Statistics Type Selector */}
        <div className="statistics-type-selector">
          <button 
            className={`statistics-type-btn ${statisticsType === 'expenses' ? 'active' : ''}`}
            onClick={() => setStatisticsType('expenses')}
          >
            Витрати
          </button>
          <button 
            className={`statistics-type-btn ${statisticsType === 'income' ? 'active' : ''}`}
            onClick={() => setStatisticsType('income')}
          >
            Доходи
          </button>
          <button 
            className={`statistics-type-btn ${statisticsType === 'balance' ? 'active' : ''}`}
            onClick={() => setStatisticsType('balance')}
          >
            Баланс
          </button>
          <button 
            className={`statistics-type-btn ${statisticsType === 'comparison' ? 'active' : ''}`}
            onClick={() => setStatisticsType('comparison')}
          >
            Порівняння
          </button>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-header">
            <div
              className="filters-toggle"
              onClick={() => setFiltersCollapsed(!filtersCollapsed)}
            >
              <span className="filters-label">Фільтри</span>
              <svg 
                className={`filters-toggle-icon ${filtersCollapsed ? '' : 'expanded'}`}
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </div>

          <div className={`filters-group ${filtersCollapsed ? 'collapsed' : ''}`}>
            {/* Period Selector */}
            {statisticsType !== 'comparison' && (
            <div className="period-selector">
              {statisticsType !== 'income' && statisticsType !== 'balance' && (
                <button 
                  className={`period-btn ${filters.period === 'week' ? 'active' : ''}`}
                  onClick={() => {
                    // Знаходимо поточний тиждень або перший доступний
                    const availableWeeks = getAvailableWeeks(availableDates);
                    let weekToSelect: string | undefined;
                    if (availableWeeks.length > 0) {
                      const currentWeek = getCurrentWeek();
                      const weekExists = availableWeeks.some(w => w.value === currentWeek);
                      weekToSelect = weekExists ? currentWeek : availableWeeks[0].value;
                    }
                    setFilters(prev => ({ 
                      ...prev, 
                      period: 'week',
                      selectedWeek: weekToSelect,
                      selectedMonth: undefined,
                      selectedYear: undefined
                    }));
                  }}
              >
                Тиждень
              </button>
              )}
              <button 
                className={`period-btn ${filters.period === 'month' ? 'active' : ''}`}
                onClick={() => {
                  // Знаходимо останній доступний місяць
                  const availableYears = getAvailableYears(availableDates);
                  if (availableYears.length > 0) {
                    const lastYear = availableYears[0].value;
                    const availableMonths = getAvailableMonths(availableDates, lastYear);
                    if (availableMonths.length > 0) {
                      setFilters(prev => ({ 
                        ...prev, 
                        period: 'month',
                        selectedWeek: undefined,
                        selectedMonth: availableMonths[0].value,
                        selectedYear: lastYear
                      }));
                    } else {
                      setFilters(prev => ({ 
                        ...prev, 
                        period: 'month',
                        selectedWeek: undefined,
                        selectedMonth: undefined,
                        selectedYear: lastYear
                      }));
                    }
                  } else {
                    setFilters(prev => ({ 
                      ...prev, 
                      period: 'month',
                      selectedWeek: undefined,
                      selectedMonth: undefined,
                      selectedYear: undefined
                    }));
                  }
              }}
              >
                Місяць
              </button>
              <button 
                className={`period-btn ${filters.period === 'year' ? 'active' : ''}`}
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  period: 'year',
                  selectedWeek: undefined,
                  selectedMonth: undefined,
                  selectedYear: prev.selectedYear ?? new Date().getFullYear()
                }))}
              >
                Рік
              </button>
              <button 
                className={`period-btn ${filters.period === 'custom' ? 'active' : ''}`}
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  period: 'custom',
                  selectedWeek: undefined,
                  selectedMonth: undefined,
                  selectedYear: undefined
                }))}
              >
                Довільний період
              </button>
            </div>
            )}

        {/* Period Specific Selectors */}
        {statisticsType !== 'comparison' && filters.period === 'week' && statisticsType !== 'income' && statisticsType !== 'balance' && (
          <div className="form-group">
            <label className="form-label">Оберіть тиждень</label>
            <GlassSelect
              options={getAvailableWeeks(availableDates).map(week => ({
                value: week.value,
                label: week.label
              }))}
              value={filters.selectedWeek || ''}
              onChange={(value) => setFilters(prev => ({ ...prev, selectedWeek: value || undefined }))}
              placeholder="Оберіть тиждень"
              disabled={loadingDates}
            />
          </div>
        )}

        {statisticsType !== 'comparison' && filters.period === 'month' && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Місяць</label>
              <GlassSelect
                options={getAvailableMonths(availableDates, filters.selectedYear).map(month => ({
                  value: String(month.value),
                  label: month.label
                }))}
                value={filters.selectedMonth !== undefined ? String(filters.selectedMonth) : ''}
                onChange={(value) => setFilters(prev => ({ 
                  ...prev, 
                  selectedMonth: value ? Number(value) : undefined 
                }))}
                placeholder="Оберіть місяць"
                disabled={loadingDates}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Рік</label>
              <GlassSelect
                options={getAvailableYears(availableDates).map(year => ({
                  value: String(year.value),
                  label: year.label
                }))}
                value={filters.selectedYear !== undefined ? String(filters.selectedYear) : ''}
                onChange={(value) => {
                  const newYear = value ? Number(value) : undefined;
                  if (newYear !== undefined) {
                    // При зміні року автоматично вибираємо останній доступний місяць для цього року
                    const availableMonths = getAvailableMonths(availableDates, newYear);
                    setFilters(prev => ({ 
                      ...prev, 
                      selectedYear: newYear,
                      selectedMonth: availableMonths.length > 0 ? availableMonths[0].value : undefined
                    }));
                  } else {
                    setFilters(prev => ({ 
                      ...prev, 
                      selectedYear: undefined,
                      selectedMonth: undefined
                    }));
                  }
                }}
                placeholder="Оберіть рік"
                disabled={loadingDates}
              />
            </div>
          </div>
        )}

        {statisticsType !== 'comparison' && filters.period === 'year' && (
          <div className="form-group">
            <label className="form-label">Оберіть рік</label>
            <GlassSelect
              options={getAvailableYears(availableDates).map(year => ({
                value: String(year.value),
                label: year.label
              }))}
              value={filters.selectedYear !== undefined ? String(filters.selectedYear) : ''}
              onChange={(value) => setFilters(prev => ({ 
                ...prev, 
                selectedYear: value ? Number(value) : undefined 
              }))}
              placeholder="Оберіть рік"
              disabled={loadingDates}
            />
          </div>
        )}

        {/* Custom Date Range */}
        {statisticsType !== 'comparison' && filters.period === 'custom' && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="date-from">З</label>
              <div className="date-input-wrapper">
                <input 
                  type="date" 
                  id="date-from"
                  className="form-input date-input-custom"
                  value={filters.dateFrom || ''}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  autoComplete="off"
                />
                <label htmlFor="date-from" className="date-input-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </label>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="date-to">До</label>
              <div className="date-input-wrapper">
                <input 
                  type="date" 
                  id="date-to"
                  className="form-input date-input-custom"
                  value={filters.dateTo || ''}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  autoComplete="off"
                />
                <label htmlFor="date-to" className="date-input-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </label>
              </div>
            </div>
          </div>
        )}

            {/* Account Filter */}
            {statisticsType !== 'comparison' && (
            <div className="account-filter">
              <label className="form-label">Рахунок</label>
              <GlassSelect
                options={[
                  { value: '', label: 'Всі рахунки' },
                  ...accounts.map(account => ({
                    value: account.id,
                    label: account.name
                  }))
                ]}
                value={filters.accountId || ''}
                onChange={(value) => setFilters({ ...filters, accountId: value || undefined })}
                placeholder="Оберіть рахунок"
              />
            </div>
            )}

            {/* Comparison Filters */}
            {statisticsType === 'comparison' && (
              <>
                <div className="form-group">
                  <label className="form-label">Тип порівняння</label>
                  <div className="comparison-type-selector">
                    <button 
                      className={`comparison-type-btn ${comparisonType === 'expenses' ? 'active' : ''}`}
                      onClick={() => setComparisonType('expenses')}
                    >
                      Витрати
                    </button>
                    <button 
                      className={`comparison-type-btn ${comparisonType === 'income' ? 'active' : ''}`}
                      onClick={() => setComparisonType('income')}
                    >
                      Доходи
                    </button>
                    <button 
                      className={`comparison-type-btn ${comparisonType === 'savings' ? 'active' : ''}`}
                      onClick={() => setComparisonType('savings')}
                    >
                      Заощадження
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Період порівняння</label>
                  <div className="comparison-period-selector">
                    {(() => {
                      const availableWeeks = getAvailableWeeks(availableDates);
                      const availableYears = getAvailableYears(availableDates);
                      const hasMultipleWeeks = availableWeeks.length > 1;
                      const hasMultipleYears = availableYears.length > 1;
                      
                      // Перевіряємо чи є кілька місяців в різних роках або в одному році
                      let hasMultipleMonths = false;
                      if (availableYears.length > 1) {
                        hasMultipleMonths = true;
                      } else if (availableYears.length === 1) {
                        const months = getAvailableMonths(availableDates, availableYears[0].value);
                        hasMultipleMonths = months.length > 1;
                      }
                      
                      return (
                        <>
                          {hasMultipleWeeks && (
                            <button 
                              className={`comparison-period-btn ${comparisonPeriod === 'week' ? 'active' : ''}`}
                              onClick={() => setComparisonPeriod('week')}
                            >
                              Тижні
                            </button>
                          )}
                          {hasMultipleMonths && (
                            <button 
                              className={`comparison-period-btn ${comparisonPeriod === 'month' ? 'active' : ''}`}
                              onClick={() => setComparisonPeriod('month')}
                            >
                              Місяці
                            </button>
                          )}
                          {hasMultipleYears && (
                            <button 
                              className={`comparison-period-btn ${comparisonPeriod === 'year' ? 'active' : ''}`}
                              onClick={() => setComparisonPeriod('year')}
                            >
                              Роки
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {comparisonType !== 'savings' && (
                  <div className="form-group">
                    <label className="form-label">Обхват порівняння</label>
                    <div className="comparison-scope-selector">
                      <button 
                        className={`comparison-scope-btn ${comparisonScope === 'global' ? 'active' : ''}`}
                        onClick={() => setComparisonScope('global')}
                      >
                        Глобальне
                      </button>
                      <button 
                        className={`comparison-scope-btn ${comparisonScope === 'category' ? 'active' : ''}`}
                        onClick={() => setComparisonScope('category')}
                      >
                        По категоріях
                      </button>
                    </div>
                  </div>
                )}

                {/* Перший період */}
                <div className="form-group">
                  <label className="form-label">Перший період</label>
                  {comparisonPeriod === 'week' && (
                    <GlassSelect
                      options={getAvailableWeeks(availableDates).map(week => ({
                        value: week.value,
                        label: week.label
                      }))}
                      value={firstPeriodWeek}
                      onChange={(value) => setFirstPeriodWeek(value || '')}
                      placeholder="Оберіть тиждень"
                      disabled={loadingDates}
                    />
                  )}
                  {comparisonPeriod === 'month' && (
                    <div className="form-row">
                      <div className="form-group">
                        <GlassSelect
                          options={getAvailableMonths(availableDates, firstPeriodYear).map(month => ({
                            value: String(month.value),
                            label: month.label
                          }))}
                          value={firstPeriodMonth !== undefined ? String(firstPeriodMonth) : ''}
                          onChange={(value) => setFirstPeriodMonth(value ? Number(value) : undefined)}
                          placeholder="Оберіть місяць"
                          disabled={loadingDates}
                        />
                      </div>
                      <div className="form-group">
                        <GlassSelect
                          options={getAvailableYears(availableDates).map(year => ({
                            value: String(year.value),
                            label: year.label
                          }))}
                          value={firstPeriodYear !== undefined ? String(firstPeriodYear) : ''}
                          onChange={(value) => {
                            const newYear = value ? Number(value) : undefined;
                            setFirstPeriodYear(newYear);
                            // Автоматично вибираємо останній доступний місяць для цього року
                            if (newYear !== undefined) {
                              const availableMonths = getAvailableMonths(availableDates, newYear);
                              if (availableMonths.length > 0 && firstPeriodMonth === undefined) {
                                setFirstPeriodMonth(availableMonths[0].value);
                              }
                            }
                          }}
                          placeholder="Оберіть рік"
                          disabled={loadingDates}
                        />
                      </div>
                    </div>
                  )}
                  {comparisonPeriod === 'year' && (
                    <GlassSelect
                      options={getAvailableYears(availableDates).map(year => ({
                        value: String(year.value),
                        label: year.label
                      }))}
                      value={firstPeriodYear !== undefined ? String(firstPeriodYear) : ''}
                      onChange={(value) => setFirstPeriodYear(value ? Number(value) : undefined)}
                      placeholder="Оберіть рік"
                      disabled={loadingDates}
                    />
                  )}
                </div>

                {/* Другий період */}
                <div className="form-group">
                  <label className="form-label">Другий період</label>
                  {comparisonPeriod === 'week' && (
                    <GlassSelect
                      options={getAvailableWeeks(availableDates).map(week => ({
                        value: week.value,
                        label: week.label
                      }))}
                      value={secondPeriodWeek}
                      onChange={(value) => setSecondPeriodWeek(value || '')}
                      placeholder="Оберіть тиждень"
                      disabled={loadingDates}
                    />
                  )}
                  {comparisonPeriod === 'month' && (
                    <div className="form-row">
                      <div className="form-group">
                        <GlassSelect
                          options={getAvailableMonths(availableDates, secondPeriodYear).map(month => ({
                            value: String(month.value),
                            label: month.label
                          }))}
                          value={secondPeriodMonth !== undefined ? String(secondPeriodMonth) : ''}
                          onChange={(value) => setSecondPeriodMonth(value ? Number(value) : undefined)}
                          placeholder="Оберіть місяць"
                          disabled={loadingDates}
                        />
                      </div>
                      <div className="form-group">
                        <GlassSelect
                          options={getAvailableYears(availableDates).map(year => ({
                            value: String(year.value),
                            label: year.label
                          }))}
                          value={secondPeriodYear !== undefined ? String(secondPeriodYear) : ''}
                          onChange={(value) => {
                            const newYear = value ? Number(value) : undefined;
                            setSecondPeriodYear(newYear);
                            // Автоматично вибираємо останній доступний місяць для цього року
                            if (newYear !== undefined) {
                              const availableMonths = getAvailableMonths(availableDates, newYear);
                              if (availableMonths.length > 0 && secondPeriodMonth === undefined) {
                                setSecondPeriodMonth(availableMonths[0].value);
                              }
                            }
                          }}
                          placeholder="Оберіть рік"
                          disabled={loadingDates}
                        />
                      </div>
                    </div>
                  )}
                  {comparisonPeriod === 'year' && (
                    <GlassSelect
                      options={getAvailableYears(availableDates).map(year => ({
                        value: String(year.value),
                        label: year.label
                      }))}
                      value={secondPeriodYear !== undefined ? String(secondPeriodYear) : ''}
                      onChange={(value) => setSecondPeriodYear(value ? Number(value) : undefined)}
                      placeholder="Оберіть рік"
                      disabled={loadingDates}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Expenses Statistics Display */}
      {statisticsType === 'expenses' && (
        <>
          {loading ? (
            <div className="card">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Завантаження статистики...</p>
              </div>
            </div>
          ) : statistics && statistics.categories.length > 0 ? (
            <div className="card">
              <div className="statistics-header">
                <h3>Витрати за категоріями</h3>
                <div className="total-amount">
                  Загальна сума: <span className="amount-value expenses">{formatCurrency(statistics.totalAmount)}</span>
                </div>
              </div>
              
              <div className="categories-list">
                {statistics.categories.map((cat) => (
                  <div 
                    key={cat.categoryId} 
                    className="category-item clickable"
                    onClick={() => {
                      setSelectedCategory({ id: cat.categoryId, name: cat.categoryName, type: 'expenses' });
                      setIsModalOpen(true);
                    }}
                  >
                    <div className="category-header">
                      <span className="category-name">{cat.categoryName}</span>
                      <span className="category-amount expenses">{formatCurrency(cat.amount)}</span>
                    </div>
                    <div className="category-info">
                      <span>Кількість позицій: {cat.transactionsCount}</span>
                      <span>{cat.percentage.toFixed(1)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill expenses" 
                        style={{ width: `${cat.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : statistics && statistics.categories.length === 0 ? (
            <div className="card">
              <div className="stats-empty-state">

                <div className="stats-empty-title">Немає даних</div>
                <div className="stats-empty-text">Для вибраного періоду немає витрат</div>
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* Income Statistics Display */}
      {statisticsType === 'income' && (
        <>
          {loading ? (
            <div className="card">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Завантаження статистики...</p>
              </div>
            </div>
          ) : incomeStatistics && incomeStatistics.categories.length > 0 ? (
            <div className="card">
              <div className="statistics-header">
                <h3>Доходи за категоріями</h3>
                <div className="total-amount">
                  Загальна сума: <span className="amount-value income">{formatCurrency(incomeStatistics.totalAmount)}</span>
                </div>
              </div>
              
              <div className="categories-list">
                {incomeStatistics.categories.map((cat) => (
                  <div 
                    key={cat.categoryId} 
                    className="category-item clickable"
                    onClick={() => {
                      setSelectedCategory({ id: cat.categoryId, name: cat.categoryName, type: 'income' });
                      setIsModalOpen(true);
                    }}
                  >
                    <div className="category-header">
                      <span className="category-name">{cat.categoryName}</span>
                      <span className="category-amount income">{formatCurrency(cat.amount)}</span>
                    </div>
                    <div className="category-info">
                      <span>Кількість позицій: {cat.transactionsCount}</span>
                      <span>{cat.percentage.toFixed(1)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill income" 
                        style={{ width: `${cat.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : incomeStatistics && incomeStatistics.categories.length === 0 ? (
            <div className="card">
              <div className="stats-empty-state">           
                <div className="stats-empty-title">Немає даних</div>
                <div className="stats-empty-text">Для вибраного періоду немає доходів</div>
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* Balance Statistics Display */}
      {statisticsType === 'balance' && (
        <>
          {loading ? (
            <div className="card">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Завантаження статистики...</p>
              </div>
            </div>
          ) : statistics && incomeStatistics ? (
            <>
              {/* Balance Summary Cards */}
              <div className="card">
                <h3 className="card-title">Баланс за період</h3>
                <div className="balance-stats-grid">
                  <div className="balance-stat balance-stat-income">
                    <div className="balance-stat-label">Доходи</div>
                    <div className="balance-stat-value balance-stat-income-value">
                      {formatCurrency(incomeStatistics.totalAmount)}
                    </div>
                  </div>
                  <div className="balance-stat balance-stat-expenses">
                    <div className="balance-stat-label">Витрати</div>
                    <div className="balance-stat-value balance-stat-expenses-value">
                      {formatCurrency(statistics.totalAmount)}
                    </div>
                  </div>
                  <div className={`balance-stat ${incomeStatistics.totalAmount >= statistics.totalAmount ? 'balance-stat-positive' : 'balance-stat-negative'}`}>
                    <div className="balance-stat-label">Баланс</div>
                    <div className={`balance-stat-value ${incomeStatistics.totalAmount >= statistics.totalAmount ? 'balance-stat-income-value' : 'balance-stat-expenses-value'}`}>
                      {formatCurrency(incomeStatistics.totalAmount - statistics.totalAmount)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Savings Cards */}
              {(() => {
                const totalSavings = transfers.reduce((sum, transfer) => sum + transfer.fromAmount, 0);
                const balance = incomeStatistics.totalAmount - statistics.totalAmount;
                const balanceAfterSavings = balance - totalSavings;
                
                return (
                  <div className="card">
                    <h3 className="card-title">Заощадження</h3>
                    {totalSavings === 0 ? (
                      <div className="savings-empty-message">
                        Немає заощаджень
                      </div>
                    ) : (
                      <div className="balance-stats-grid savings-stats-grid">
                        <div className="balance-stat balance-stat-savings">
                          <div className="balance-stat-label">Заощадження</div>
                          <div className="balance-stat-value balance-stat-savings-value">
                            {formatCurrency(totalSavings)}
                          </div>
                        </div>
                        <div className={`balance-stat ${balanceAfterSavings >= 0 ? 'balance-stat-positive' : 'balance-stat-negative'}`}>
                          <div className="balance-stat-label">Баланс після заощаджень</div>
                          <div className={`balance-stat-value ${balanceAfterSavings >= 0 ? 'balance-stat-income-value' : 'balance-stat-expenses-value'}`}>
                            {formatCurrency(balanceAfterSavings)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Top Categories */}
              {(incomeStatistics.categories.length > 0 || statistics.categories.length > 0) && (
                <div className="card">
                  <h3 className="card-title">Топ категорій</h3>
                  
                  {incomeStatistics.categories.length > 0 && (
                    <div className="balance-section">
                      <h4 className="balance-section-title income">Топ доходів</h4>
                      <div className="categories-list">
                        {incomeStatistics.categories.slice(0, 5).map((cat) => (
                          <div 
                            key={`income-${cat.categoryId}`} 
                            className="category-item clickable"
                            onClick={() => {
                              setSelectedCategory({ id: cat.categoryId, name: cat.categoryName, type: 'income' });
                              setIsModalOpen(true);
                            }}
                          >
                            <div className="category-header">
                              <span className="category-name">{cat.categoryName}</span>
                              <span className="category-amount income">{formatCurrency(cat.amount)}</span>
                            </div>
                            <div className="category-info">
                              <span>Кількість позицій: {cat.transactionsCount}</span>
                              <span>{cat.percentage.toFixed(1)}%</span>
                            </div>
                            <div className="progress-bar">
                              <div 
                                className="progress-fill income" 
                                style={{ width: `${cat.percentage}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {statistics.categories.length > 0 && (
                    <div className="balance-section">
                      <h4 className="balance-section-title expenses">Топ витрат</h4>
                      <div className="categories-list">
                        {statistics.categories.slice(0, 5).map((cat) => (
                          <div 
                            key={`expense-${cat.categoryId}`} 
                            className="category-item clickable"
                            onClick={() => {
                              setSelectedCategory({ id: cat.categoryId, name: cat.categoryName, type: 'expenses' });
                              setIsModalOpen(true);
                            }}
                          >
                            <div className="category-header">
                              <span className="category-name">{cat.categoryName}</span>
                              <span className="category-amount expenses">{formatCurrency(cat.amount)}</span>
                            </div>
                            <div className="category-info">
                              <span>Кількість позицій: {cat.transactionsCount}</span>
                              <span>{cat.percentage.toFixed(1)}%</span>
                            </div>
                            <div className="progress-bar">
                              <div 
                                className="progress-fill expenses" 
                                style={{ width: `${cat.percentage}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </>
      )}

      {/* Comparison Statistics Display */}
      {statisticsType === 'comparison' && (
        <>
          {loading ? (
            <div className="card">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Завантаження порівняння...</p>
              </div>
            </div>
          ) : (
            <>
              {comparisonType === 'expenses' && currentPeriodData && previousPeriodData && (
                <>
                  {comparisonScope === 'global' ? (
                    <div className="card">
                      <h3 className="card-title">Порівняння витрат</h3>
                      <div className="comparison-table-wrapper">
                        <table className="comparison-table">
                          <thead>
                            <tr>
                              <th>
                                Перший період
                                {currentPeriodData.dateFrom && currentPeriodData.dateTo && (
                                  <div className="comparison-table-header-date">
                                    {formatDate(currentPeriodData.dateFrom)} - {formatDate(currentPeriodData.dateTo)}
                                  </div>
                                )}
                              </th>
                              <th>
                                Другий період
                                {previousPeriodData.dateFrom && previousPeriodData.dateTo && (
                                  <div className="comparison-table-header-date">
                                    {formatDate(previousPeriodData.dateFrom)} - {formatDate(previousPeriodData.dateTo)}
                                  </div>
                                )}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                <div className="comparison-table-cell">
                                  <div className="comparison-table-value">
                                    {formatCurrency(currentPeriodData.totalAmount)}
                                  </div>
                                </div>
                              </td>
                              <td>
                                {(() => {
                                  const first = currentPeriodData.totalAmount;
                                  const second = previousPeriodData.totalAmount;
                                  const change = first - second;
                                  const percentageChange = second !== 0 
                                    ? ((change / second) * 100)
                                    : (first > 0 ? 100 : 0);
                                  const isIncrease = change > 0;
                                  const isEqual = change === 0;
                                  
                                  return (
                                    <div className={`comparison-table-value expenses ${isIncrease ? 'increase' : isEqual ? '' : 'decrease'}`}>
                                      {formatCurrency(second)}
                                      {!isEqual && (
                                        <span className={`comparison-table-change-inline ${isIncrease ? 'increase' : 'decrease'}`}>
                                          {' '}({isIncrease ? '+' : ''}{percentageChange.toFixed(1)}%)
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="card">
                        <h3 className="card-title">Порівняння витрат по категоріях</h3>
                        
                        {/* Filter and Sort Controls */}
                        <div className="comparison-controls">
                          <div className="comparison-filter">
                            <input
                              type="text"
                              className="comparison-filter-input"
                              placeholder="Пошук категорії..."
                              value={categoryFilter}
                              onChange={(e) => setCategoryFilter(e.target.value)}
                            />
                          </div>
                        </div>

                      <div className="comparison-table-wrapper">
                        <table className="comparison-table">
                          <thead>
                            <tr>
                              <th 
                                className="sortable"
                                onClick={() => {
                                  if (sortColumn === 'name') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortColumn('name');
                                    setSortDirection('asc');
                                  }
                                }}
                              >
                                Категорія
                                {sortColumn === 'name' && (
                                  <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                )}
                              </th>
                              <th 
                                className="sortable"
                                onClick={() => {
                                  if (sortColumn === 'first') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortColumn('first');
                                    setSortDirection('desc');
                                  }
                                }}
                              >
                                Перший період
                                {sortColumn === 'first' && (
                                  <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                )}
                              </th>
                              <th 
                                className="sortable"
                                onClick={() => {
                                  if (sortColumn === 'second') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortColumn('second');
                                    setSortDirection('desc');
                                  }
                                }}
                              >
                                Другий період
                                {sortColumn === 'second' && (
                                  <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                )}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const categoryMap = new Map<string, { current: number; previous: number; name: string }>();
                              
                              currentPeriodData.categories.forEach(cat => {
                                categoryMap.set(cat.categoryId, {
                                  current: cat.amount,
                                  previous: 0,
                                  name: cat.categoryName
                                });
                              });
                              
                              previousPeriodData.categories.forEach(cat => {
                                const existing = categoryMap.get(cat.categoryId);
                                if (existing) {
                                  existing.previous = cat.amount;
                                } else {
                                  categoryMap.set(cat.categoryId, {
                                    current: 0,
                                    previous: cat.amount,
                                    name: cat.categoryName
                                  });
                                }
                              });
                              
                              let categories = Array.from(categoryMap.values())
                                .filter(item => item.current > 0 || item.previous > 0)
                                .filter(item => 
                                  categoryFilter === '' || 
                                  item.name.toLowerCase().includes(categoryFilter.toLowerCase())
                                );

                              // Calculate totals
                              const totals = categories.reduce((acc, item) => {
                                acc.first += item.current;
                                acc.second += item.previous;
                                return acc;
                              }, { first: 0, second: 0 });

                              // Sort categories
                              categories = categories.sort((a, b) => {
                                const aChange = a.current - a.previous;
                                const bChange = b.current - b.previous;
                                const aPercentage = a.previous !== 0 ? ((aChange / a.previous) * 100) : (a.current > 0 ? 100 : 0);
                                const bPercentage = b.previous !== 0 ? ((bChange / b.previous) * 100) : (b.current > 0 ? 100 : 0);
                                
                                let comparison = 0;
                                switch (sortColumn) {
                                  case 'name':
                                    comparison = a.name.localeCompare(b.name);
                                    break;
                                  case 'first':
                                    comparison = a.current - b.current;
                                    break;
                                  case 'second':
                                    comparison = a.previous - b.previous;
                                    break;
                                  case 'change':
                                    comparison = Math.abs(aChange) - Math.abs(bChange);
                                    break;
                                  case 'percentage':
                                    comparison = Math.abs(aPercentage) - Math.abs(bPercentage);
                                    break;
                                }
                                
                                return sortDirection === 'asc' ? comparison : -comparison;
                              });
                              
                              return (
                                <>
                                  {categories.map((item, index) => {
                                    const change = item.current - item.previous;
                                    const percentageChange = item.previous !== 0 
                                      ? ((change / item.previous) * 100)
                                      : item.current > 0 ? 100 : 0;
                                    const isIncrease = change > 0;
                                    const isEqual = change === 0;
                                    const absPercentage = Math.abs(percentageChange);
                                    const changeIntensity = absPercentage > 50 ? 'high' : absPercentage > 20 ? 'medium' : 'low';
                                  
                                  return (
                                    <tr key={`comparison-${index}`}>
                                      <td>
                                        <div className="comparison-table-category">{item.name}</div>
                                      </td>
                                      <td>
                                        <div className="comparison-table-value">
                                          {formatCurrency(item.current)}
                                        </div>
                                      </td>
                                      <td>
                                        <div className={`comparison-table-value expenses ${isIncrease ? 'increase' : isEqual ? '' : 'decrease'} ${changeIntensity}`}>
                                          {formatCurrency(item.previous)}
                                          {!isEqual && (
                                            <span className={`comparison-table-change-inline ${isIncrease ? 'increase' : 'decrease'}`}>
                                              {' '}({isIncrease ? '+' : ''}{percentageChange.toFixed(1)}%)
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      </tr>
                                    );
                                  })}
                                </>
                              );
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    </>
                  )}
                </>
              )}

              {comparisonType === 'income' && currentPeriodIncome && previousPeriodIncome && (
                <>
                  {comparisonScope === 'global' ? (
                    <div className="card">
                      <h3 className="card-title">Порівняння доходів</h3>
                      <div className="comparison-table-wrapper">
                        <table className="comparison-table">
                          <thead>
                            <tr>
                              <th>
                                Перший період
                                {currentPeriodIncome.dateFrom && currentPeriodIncome.dateTo && (
                                  <div className="comparison-table-header-date">
                                    {formatDate(currentPeriodIncome.dateFrom)} - {formatDate(currentPeriodIncome.dateTo)}
                                  </div>
                                )}
                              </th>
                              <th>
                                Другий період
                                {previousPeriodIncome.dateFrom && previousPeriodIncome.dateTo && (
                                  <div className="comparison-table-header-date">
                                    {formatDate(previousPeriodIncome.dateFrom)} - {formatDate(previousPeriodIncome.dateTo)}
                                  </div>
                                )}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                <div className="comparison-table-cell">
                                  <div className="comparison-table-value">
                                    {formatCurrency(currentPeriodIncome.totalAmount)}
                                  </div>
                                </div>
                              </td>
                              <td>
                                {(() => {
                                  const first = currentPeriodIncome.totalAmount;
                                  const second = previousPeriodIncome.totalAmount;
                                  const change = first - second;
                                  const percentageChange = second !== 0 
                                    ? ((change / second) * 100)
                                    : (first > 0 ? 100 : 0);
                                  // Для доходів збільшення - це добре (зелений), зменшення - погано (червоний)
                                  const isGood = change < 0; // Якщо другий період більший за перший - це добре
                                  const isEqual = change === 0;
                                  // Інвертуємо відсоток для правильного відображення
                                  const displayPercentage = -percentageChange;
                                  
                                  return (
                                    <div className={`comparison-table-value income ${isGood ? 'increase' : isEqual ? '' : 'decrease'}`}>
                                      {formatCurrency(second)}
                                      {!isEqual && (
                                        <span className={`comparison-table-change-inline ${isGood ? 'increase' : 'decrease'}`}>
                                          {' '}({displayPercentage > 0 ? '+' : ''}{displayPercentage.toFixed(1)}%)
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="card">
                        <h3 className="card-title">Порівняння доходів по категоріях</h3>
                        
                        {/* Filter and Sort Controls */}
                        <div className="comparison-controls">
                          <div className="comparison-filter">
                            <input
                              type="text"
                              className="comparison-filter-input"
                              placeholder="Пошук категорії..."
                              value={categoryFilter}
                              onChange={(e) => setCategoryFilter(e.target.value)}
                            />
                          </div>
                        </div>

                      <div className="comparison-table-wrapper">
                        <table className="comparison-table">
                          <thead>
                            <tr>
                              <th 
                                className="sortable"
                                onClick={() => {
                                  if (sortColumn === 'name') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortColumn('name');
                                    setSortDirection('asc');
                                  }
                                }}
                              >
                                Категорія
                                {sortColumn === 'name' && (
                                  <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                )}
                              </th>
                              <th 
                                className="sortable"
                                onClick={() => {
                                  if (sortColumn === 'first') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortColumn('first');
                                    setSortDirection('desc');
                                  }
                                }}
                              >
                                Перший період
                                {sortColumn === 'first' && (
                                  <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                )}
                              </th>
                              <th 
                                className="sortable"
                                onClick={() => {
                                  if (sortColumn === 'second') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortColumn('second');
                                    setSortDirection('desc');
                                  }
                                }}
                              >
                                Другий період
                                {sortColumn === 'second' && (
                                  <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                )}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const categoryMap = new Map<string, { current: number; previous: number; name: string }>();
                              
                              currentPeriodIncome.categories.forEach(cat => {
                                categoryMap.set(cat.categoryId, {
                                  current: cat.amount,
                                  previous: 0,
                                  name: cat.categoryName
                                });
                              });
                              
                              previousPeriodIncome.categories.forEach(cat => {
                                const existing = categoryMap.get(cat.categoryId);
                                if (existing) {
                                  existing.previous = cat.amount;
                                } else {
                                  categoryMap.set(cat.categoryId, {
                                    current: 0,
                                    previous: cat.amount,
                                    name: cat.categoryName
                                  });
                                }
                              });
                              
                              let categories = Array.from(categoryMap.values())
                                .filter(item => item.current > 0 || item.previous > 0)
                                .filter(item => 
                                  categoryFilter === '' || 
                                  item.name.toLowerCase().includes(categoryFilter.toLowerCase())
                                );

                              // Calculate totals
                              const totals = categories.reduce((acc, item) => {
                                acc.first += item.current;
                                acc.second += item.previous;
                                return acc;
                              }, { first: 0, second: 0 });

                              // Sort categories
                              categories = categories.sort((a, b) => {
                                const aChange = a.current - a.previous;
                                const bChange = b.current - b.previous;
                                const aPercentage = a.previous !== 0 ? ((aChange / a.previous) * 100) : (a.current > 0 ? 100 : 0);
                                const bPercentage = b.previous !== 0 ? ((bChange / b.previous) * 100) : (b.current > 0 ? 100 : 0);
                                
                                let comparison = 0;
                                switch (sortColumn) {
                                  case 'name':
                                    comparison = a.name.localeCompare(b.name);
                                    break;
                                  case 'first':
                                    comparison = a.current - b.current;
                                    break;
                                  case 'second':
                                    comparison = a.previous - b.previous;
                                    break;
                                  case 'change':
                                    comparison = Math.abs(aChange) - Math.abs(bChange);
                                    break;
                                  case 'percentage':
                                    comparison = Math.abs(aPercentage) - Math.abs(bPercentage);
                                    break;
                                }
                                
                                return sortDirection === 'asc' ? comparison : -comparison;
                              });
                              
                              return (
                                <>
                                  {categories.map((item, index) => {
                                    const change = item.current - item.previous;
                                    const percentageChange = item.previous !== 0 
                                      ? ((change / item.previous) * 100)
                                      : item.current > 0 ? 100 : 0;
                                    // Для доходів збільшення - це добре (зелений), зменшення - погано (червоний)
                                    const isGood = change < 0; // Якщо другий період більший за перший - це добре
                                    const isEqual = change === 0;
                                    // Інвертуємо відсоток для правильного відображення
                                    const displayPercentage = -percentageChange;
                                    const absPercentage = Math.abs(displayPercentage);
                                    const changeIntensity = absPercentage > 50 ? 'high' : absPercentage > 20 ? 'medium' : 'low';
                                  
                                    return (
                                      <tr key={`comparison-income-${index}`}>
                                        <td>
                                          <div className="comparison-table-category">{item.name}</div>
                                        </td>
                                        <td>
                                          <div className="comparison-table-value">
                                            {formatCurrency(item.current)}
                                          </div>
                                        </td>
                                        <td>
                                          <div className={`comparison-table-value income ${isGood ? 'increase' : isEqual ? '' : 'decrease'} ${changeIntensity}`}>
                                            {formatCurrency(item.previous)}
                                            {!isEqual && (
                                              <span className={`comparison-table-change-inline ${isGood ? 'increase' : 'decrease'}`}>
                                                {' '}({displayPercentage > 0 ? '+' : ''}{displayPercentage.toFixed(1)}%)
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                    </tr>
                                  );
                                  })}
                                </>
                              );
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    </>
                  )}
                </>
              )}

              {comparisonType === 'savings' && currentPeriodTransfers && previousPeriodTransfers && (
                <div className="card">
                  <h3 className="card-title">Порівняння заощаджень</h3>
                  <div className="comparison-table-wrapper">
                    <table className="comparison-table">
                      <thead>
                        <tr>
                          <th>Перший період</th>
                          <th>Другий період</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <div className="comparison-table-cell">
                              <div className="comparison-table-value">
                                {formatCurrency(currentPeriodTransfers.reduce((sum, t) => sum + t.fromAmount, 0))}
                              </div>
                            </div>
                          </td>
                          <td>
                            {(() => {
                              const first = currentPeriodTransfers.reduce((sum, t) => sum + t.fromAmount, 0);
                              const second = previousPeriodTransfers.reduce((sum, t) => sum + t.fromAmount, 0);
                              const change = first - second;
                              const percentageChange = second !== 0 
                                ? ((change / second) * 100)
                                : (first > 0 ? 100 : 0);
                              // Для заощаджень збільшення - це добре (зелений), зменшення - погано (червоний)
                              const isGood = change < 0; // Якщо другий період більший за перший - це добре
                              const isEqual = change === 0;
                              // Інвертуємо відсоток для правильного відображення
                              const displayPercentage = -percentageChange;
                              
                              return (
                                <div className={`comparison-table-value ${isGood ? 'increase' : isEqual ? '' : 'decrease'}`}>
                                  {formatCurrency(second)}
                                  {!isEqual && (
                                    <span className={`comparison-table-change-inline ${isGood ? 'increase' : 'decrease'}`}>
                                      {' '}({displayPercentage > 0 ? '+' : ''}{displayPercentage.toFixed(1)}%)
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}


      {/* Category Transactions Modal */}
      {selectedCategory && (
        <CategoryTransactionsModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedCategory(null);
          }}
          categoryId={selectedCategory.id}
          categoryName={selectedCategory.name}
          filters={{
            ...filters,
            ...(filters.period !== 'custom' 
              ? getDateRange(
                  filters.period,
                  filters.selectedMonth,
                  filters.selectedYear,
                  filters.selectedWeek
                )
              : { dateFrom: filters.dateFrom!, dateTo: filters.dateTo! })
          }}
          transactionType={selectedCategory?.type || (statisticsType === 'expenses' ? 'expenses' : statisticsType === 'income' ? 'income' : statisticsType === 'comparison' && comparisonType === 'expenses' ? 'expenses' : statisticsType === 'comparison' && comparisonType === 'income' ? 'income' : undefined)}
        />
      )}
    </div>
  );
};

export default StatisticsPage;

